// Crypto configuration constants
export const CRYPTO_CONFIG = {
  algorithm: 'AES-GCM' as const,
  keyLength: 256,
  ivLength: 12,
  saltLength: 32,
  pbkdf2Iterations: 300000,
} as const;

// Progressive delays for rate limiting (in milliseconds)
export const PROGRESSIVE_DELAYS = [
  1000,    // 1 second
  2000,    // 2 seconds
  5000,    // 5 seconds
  10000,   // 10 seconds
  30000,   // 30 seconds
  60000,   // 1 minute
  300000,  // 5 minutes
  900000,  // 15 minutes
  1800000, // 30 minutes
  3600000, // 1 hour
];

// Custom error class for crypto operations
export class CryptoError extends Error {
  public readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'CryptoError';
    this.code = code;
  }
}

// Encrypted data structure
export interface EncryptedData {
  ciphertext: Uint8Array;
  iv: Uint8Array;
}

// User profile interface
export interface UserProfile {
  id: string;
  username: string;
  createdAt: string;
  lastLogin: string;
  loginCount: number;
  passwordHash: string;
  salt: Uint8Array;
  wrappedMasterKey: Uint8Array;
}

// User portfolio data interface
export interface UserPortfolioData {
  userId: string;
  version: number;
  encryptedPortfolio: EncryptedData;
  lastModified: string;
}

// Storage message types
export type StorageMessageType = 
  | 'REGISTER'
  | 'LOGIN'
  | 'LIST_USERS'
  | 'SAVE_PORTFOLIO'
  | 'GET_PORTFOLIO'
  | 'EXPORT_USER'
  | 'DELETE_USER';

// Storage message interface
export interface StorageMessage<T> {
  type: StorageMessageType;
  requestId?: string;
  data: T;
}

// Storage response interface
export interface StorageResponse<R> {
  type: 'RESPONSE' | 'ERROR';
  data?: R;
  error?: string;
  code?: string;
  requestId?: string | undefined;
}

// Session data interface
export interface SessionData {
  userId: string;
  loginTime: number;
}

// Safe user data (without sensitive information)
export interface SafeUserData {
  id: string;
  username: string;
  createdAt: string;
  lastLogin: string;
  loginCount: number;
}

// Login response interface
export interface LoginResponse {
  success: boolean;
  user: SafeUserData;
  portfolioData: any | null;
  sessionData: SessionData;
}

// Rate limit check result
export interface RateLimitResult {
  allowed: boolean;
  delay: number;
}

// Export data structure
export interface ExportData {
  version: number;
  exportDate: string;
  userData: {
    user: UserProfile;
    portfolio: UserPortfolioData | null;
  };
}
